class LoopComponent {
  constructor(root, options = {}) {
    this.root = root;
    this.track = root.querySelector(options.trackSelector);
    this.originalSlides = Array.from(this.track.children);
    this.slides = Array.from(this.track.children);
    this.prev = root.querySelector(options.prevSelector);
    this.next = root.querySelector(options.nextSelector);
    this.gap = options.gap || 0;
    this.getVisible = options.getVisible || (() => 1);
    this.infinite = options.infinite !== false;
    this.keyboard = Boolean(options.keyboard);
    this.pointerEnabled = options.pointerEnabled !== false;
    this.activeIndex = 0;
    this.cloned = false;
    this.slideWidth = 0;
    this.step = 0;
    this.visible = 1;
    this.dragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.initialTranslate = 0;
    this.pressedPointerId = null;
    this.setup();
  }

  setup() {
    if (!this.track || !this.slides.length) return;
    this.attachEvents();
    this.rebuild();
    this.goTo(this.visible, false);
  }

  attachEvents() {
    this.prev?.addEventListener("click", () => this.prevSlide());
    this.next?.addEventListener("click", () => this.nextSlide());

    if (this.keyboard) {
      this.root.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          this.prevSlide();
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          this.nextSlide();
        }
      });
    }

    if (this.pointerEnabled) {
      this.track.addEventListener("pointerdown", (event) => this.onPointerDown(event));
      this.track.addEventListener("pointermove", (event) => this.onPointerMove(event));
      this.track.addEventListener("pointerup", (event) => this.onPointerUp(event));
      this.track.addEventListener("pointercancel", (event) => this.onPointerUp(event));
      this.track.addEventListener("pointerleave", (event) => this.onPointerUp(event));
    }

    window.addEventListener("resize", () => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.rebuild(true));
    });

    this.track.addEventListener("transitionend", () => {
      if (!this.infinite) return;
      const count = this.originalCount;
      if (this.activeIndex >= count + this.visible) {
        this.activeIndex = this.visible;
        this.setTranslate(false);
      }
      if (this.activeIndex < this.visible) {
        this.activeIndex = count + this.visible - 1;
        this.setTranslate(false);
      }
    });
  }

  rebuild(preservePosition = false) {
    const existingRelative = preservePosition
      ? Math.max(0, Math.min(this.originalCount - 1, this.activeIndex - this.visible))
      : 0;
    this.visible = Math.max(1, this.getVisible());
    this.root.style.setProperty("--visible", String(this.visible));
    this.originalCount = this.originalSlides.length;
    this.track.innerHTML = "";

    if (this.infinite) {
      const prepend = this.originalSlides.slice(-this.visible);
      const append = this.originalSlides.slice(0, this.visible);
      prepend.forEach((slide) => this.track.appendChild(slide.cloneNode(true)));
      this.originalSlides.forEach((slide) => this.track.appendChild(slide.cloneNode(true)));
      append.forEach((slide) => this.track.appendChild(slide.cloneNode(true)));
      this.activeIndex = existingRelative + this.visible;
      this.cloned = true;
    } else {
      this.originalSlides.forEach((slide) => this.track.appendChild(slide.cloneNode(true)));
      this.activeIndex = existingRelative;
    }

    this.slides = Array.from(this.track.children);
    const gap = parseFloat(getComputedStyle(this.track).gap || getComputedStyle(this.track).columnGap || "0") || 0;
    this.slideWidth = this.slides[0].getBoundingClientRect().width;
    this.step = this.slideWidth + gap;
    this.setTranslate(false);
  }

  normalizeIndex(index) {
    const count = this.originalCount || this.slides.length;
    const min = 0;
    const max = this.infinite ? count + this.visible * 2 - 1 : count - 1;
    return Math.min(max, Math.max(min, index));
  }

  setTranslate(animate = true) {
    this.track.style.transition = animate ? "" : "none";
    const translate = -(this.activeIndex * this.step);
    this.currentTranslate = translate;
    this.track.style.transform = `translateX(${translate}px)`;
    if (!animate) {
      this.track.offsetHeight;
      this.track.style.transition = "";
    }
  }

  goTo(index, animate = true) {
    this.activeIndex = this.normalizeIndex(index);
    this.setTranslate(animate);
  }

  prevSlide() {
    this.goTo(this.activeIndex - 1);
  }

  nextSlide() {
    this.goTo(this.activeIndex + 1);
  }

  onPointerDown(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    this.dragging = true;
    this.startX = event.clientX;
    this.initialTranslate = this.currentTranslate;
    this.track.classList.add("is-dragging");
    this.track.setPointerCapture?.(event.pointerId);
    this.pressedPointerId = event.pointerId;
  }

  onPointerMove(event) {
    if (!this.dragging) return;
    if (this.pressedPointerId !== null && event.pointerId !== this.pressedPointerId) return;
    const delta = event.clientX - this.startX;
    this.currentTranslate = this.initialTranslate + delta;
    this.track.style.transition = "none";
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  onPointerUp(event) {
    if (!this.dragging) return;
    if (this.pressedPointerId !== null && event.pointerId !== this.pressedPointerId) return;
    const delta = event.clientX - this.startX;
    const threshold = Math.max(40, this.step * 0.18);
    this.track.classList.remove("is-dragging");
    this.dragging = false;
    this.pressedPointerId = null;

    if (Math.abs(delta) > threshold) {
      if (delta < 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
    } else {
      this.setTranslate(true);
    }
  }
}

function initCategoriesSlider() {
  const root = document.querySelector('[data-slider="categories"]');
  if (!root) return;

  const track = root.querySelector(".slider-track");
  const slideCurrent = root.querySelector(".slide-current");
  const slideTotal = root.querySelector(".slide-total");

  if (!track) return;

  let slides = Array.from(root.querySelectorAll(".slider-slide"));
  let active = 0;
  let autoSlideInterval = null;
  let isHovering = false;
  let startX = 0;
  let dragging = false;
  let pointerId = null;

  function syncSlides(startIndex = 0) {
    slides = Array.from(root.querySelectorAll(".slider-slide"));
    active = Math.max(0, Math.min(startIndex, Math.max(0, slides.length - 1)));

    if (slideTotal) {
      slideTotal.textContent = slides.length || 1;
    }

    update(false);
  }

  function update(animate = true) {
    if (!slides.length) return;

    if (active >= slides.length) {
      active = 0;
    }

    track.style.transition = animate ? "" : "none";
    track.style.transform = `translateX(${-active * 100}%)`;

    slides.forEach((slide, index) => {
      slide.classList.toggle("is-active", index === active);
    });

    if (slideCurrent) {
      slideCurrent.textContent = active + 1;
    }

    if (!animate) {
      track.offsetHeight;
      track.style.transition = "";
    }
  }

  function step(direction) {
    if (!slides.length) return;

    active = (active + direction + slides.length) % slides.length;
    update(true);
    resetAutoSlide();
  }

  function autoSlide() {
    if (!isHovering && slides.length > 1) {
      step(1);
    }
  }

  function startAutoSlide() {
    if (autoSlideInterval) clearInterval(autoSlideInterval);
    autoSlideInterval = setInterval(autoSlide, 4000);
  }

  function resetAutoSlide() {
    if (autoSlideInterval) clearInterval(autoSlideInterval);
    startAutoSlide();
  }

  function stopAutoSlide() {
    if (autoSlideInterval) clearInterval(autoSlideInterval);
    autoSlideInterval = null;
  }

  window.WhitefitPauseCategorySlider = function () {
    stopAutoSlide();
  };

  window.WhitefitRefreshCategorySlider = function (startIndex = 0) {
    syncSlides(startIndex);
    resetAutoSlide();
  };

  root.addEventListener("mouseenter", () => {
    isHovering = true;
  });

  root.addEventListener("mouseleave", () => {
    isHovering = false;
  });

  track.addEventListener("pointerdown", (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;

    dragging = true;
    startX = event.clientX;
    pointerId = event.pointerId;
    track.classList.add("is-dragging");
    track.setPointerCapture?.(event.pointerId);
    isHovering = true;
  });

  track.addEventListener("pointermove", (event) => {
    if (!dragging || pointerId !== event.pointerId) return;

    const delta = event.clientX - startX;
    track.style.transition = "none";
    track.style.transform = `translateX(calc(${dirSign() * active * 100}% + ${delta}px))`;
  });

  function finish(event) {
    if (!dragging || pointerId !== event.pointerId) return;

    const delta = event.clientX - startX;
    dragging = false;
    pointerId = null;
    track.classList.remove("is-dragging");
    isHovering = false;

    if (Math.abs(delta) > 40) {
      const forward = isRTL() ? delta > 0 : delta < 0;
      step(forward ? 1 : -1);
    } else {
      update(true);
    }

    resetAutoSlide();
  }

  track.addEventListener("pointerup", finish);
  track.addEventListener("pointercancel", finish);
  track.addEventListener("pointerleave", finish);

  syncSlides();
  startAutoSlide();
}

function initWhoSlider() {
  const root = document.querySelector('[data-slider="who-are"]');
  if (!root) return;

  const section = root.closest(".who-section");
  const backdrop = section?.querySelector(".who-backdrop");
  const accentOne = section?.querySelector(".who-accent-one");
  const accentTwo = section?.querySelector(".who-accent-two");
  const accentImageOne = accentOne?.querySelector(".who-accent-image");
  const accentImageTwo = accentTwo?.querySelector(".who-accent-image");
  const slides = Array.from(root.querySelectorAll(".slider-slide"));
  const track = root.querySelector(".slider-track");
  const prev = root.querySelector(".slider-arrow.prev");
  const next = root.querySelector(".slider-arrow.next");
  const slideImages = slides.map((slide) => slide.dataset.whoImage || "");
  const prevIcon = prev?.querySelector("img");
  const nextIcon = next?.querySelector("img");
  let active = 0;
  let backdropRequestId = 0;
  const isRTL = () => getComputedStyle(track).direction === "rtl";
  const dirSign = () => (isRTL() ? 1 : -1);

  if (backdrop) {
    backdrop.dataset.currentSrc = backdrop.getAttribute("src") || "";
  }

  const fallbackImage = backdrop?.dataset.currentSrc || backdrop?.getAttribute("src") || "";
  const resolvedSlideImages = slideImages.map((src) => src || fallbackImage);

  function preloadImage(src) {
    if (!src) return;
    const image = new Image();
    image.src = src;
  }

  resolvedSlideImages.forEach(preloadImage);

  function findNeighborImage(index, direction) {
    if (!resolvedSlideImages.length) return "";

    let nextIndex = index;

    for (let i = 0; i < resolvedSlideImages.length; i++) {
      nextIndex = (nextIndex + direction + resolvedSlideImages.length) % resolvedSlideImages.length;

      if (resolvedSlideImages[nextIndex]) {
        return resolvedSlideImages[nextIndex];
      }
    }

    return fallbackImage;
  }

  function applyAccentImage(image, src) {
    if (!image) return;

    if (!src) {
      image.removeAttribute("src");
      return;
    }

    image.src = src;
  }

  function syncBackdrop(index, animate = true) {
    if (!backdrop) return;
    const nextSrc = resolvedSlideImages[index] || fallbackImage;
    if (!nextSrc) return;

    const applySrc = () => {
      backdrop.src = nextSrc;
      backdrop.dataset.currentSrc = nextSrc;
    };

    if (!animate) {
      backdropRequestId += 1;
      applySrc();
      return;
    }

    backdrop.classList.add("is-updating");
    const requestId = ++backdropRequestId;
    const image = new Image();
    image.onload = () => {
      if (requestId !== backdropRequestId) return;
      applySrc();
      requestAnimationFrame(() => {
        if (requestId !== backdropRequestId) return;
        backdrop.classList.remove("is-updating");
      });
    };
    image.onerror = () => {
      if (requestId !== backdropRequestId) return;
      backdrop.classList.remove("is-updating");
    };
    image.src = nextSrc;
  }

  function syncAccents(index) {
    applyAccentImage(accentImageOne, findNeighborImage(index, -1));
    applyAccentImage(accentImageTwo, findNeighborImage(index, 1));
  }

  function syncArrowState() {
    const onFirstSlide = active === 0;

    prev?.classList.toggle("is-accent", onFirstSlide);
    next?.classList.remove("is-accent");
  }

  function update(animate = true) {
    track.style.transition = animate ? "" : "none";
    track.style.transform = `translateX(${dirSign() * active * 100}%)`;
    slides.forEach((slide, index) => slide.classList.toggle("is-active", index === active));
    syncAccents(active);
    syncBackdrop(active, animate);
    syncArrowState();
    if (!animate) {
      track.offsetHeight;
      track.style.transition = "";
    }
  }

  function step(direction) {
    active = (active + direction + slides.length) % slides.length;
    update(true);
  }

  prev.addEventListener("click", () => step(-1));
  next.addEventListener("click", () => step(1));

  root.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      step(-1);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      step(1);
    }
  });

  let startX = 0;
  let dragging = false;
  let pointerId = null;

  track.addEventListener("pointerdown", (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    dragging = true;
    startX = event.clientX;
    pointerId = event.pointerId;
    track.classList.add("is-dragging");
    track.setPointerCapture?.(event.pointerId);
  });

  track.addEventListener("pointermove", (event) => {
    if (!dragging || pointerId !== event.pointerId) return;
    const delta = event.clientX - startX;
    track.style.transition = "none";
    track.style.transform = `translateX(calc(${dirSign() * active * 100}% + ${delta}px))`;
  });

  function finish(event) {
    if (!dragging || pointerId !== event.pointerId) return;
    const delta = event.clientX - startX;
    dragging = false;
    pointerId = null;
    track.classList.remove("is-dragging");
    if (Math.abs(delta) > 40) {
      step(delta < 0 ? 1 : -1);
    } else {
      update(true);
    }
  }

  track.addEventListener("pointerup", finish);
  track.addEventListener("pointercancel", finish);
  track.addEventListener("pointerleave", finish);

  update(false);
}

function initProductsCarousel() {
  const root = document.querySelector('[data-carousel="products"]');
  if (!root) return;

  const getVisible = () => {
    const width = window.innerWidth;
    if (width < 720) return 1;
    if (width < 1100) return 2;
    return 4;
  };

  const carousel = new LoopComponent(root, {
    trackSelector: ".carousel-track",
    prevSelector: ".carousel-arrow.prev",
    nextSelector: ".carousel-arrow.next",
    getVisible,
    infinite: true,
    keyboard: false,
    pointerEnabled: true,
  });

  root.addEventListener("mouseenter", () => {
    root.querySelectorAll(".wish-btn").forEach((button) => button.setAttribute("tabindex", "0"));
  });

  return carousel;
}

function initSuccessStoriesSlider() {
  const root = document.querySelector('[data-slider="success-stories"]');
  if (!root) return;

  const getVisible = () => {
    const width = window.innerWidth;
    if (width < 720) return 1;
    if (width < 1100) return 2;
    return 3;
  };

  const carousel = new LoopComponent(root, {
    trackSelector: ".carousel-track",
    prevSelector: ".carousel-arrow.prev",
    nextSelector: ".carousel-arrow.next",
    getVisible,
    infinite: true,
    keyboard: true,
    pointerEnabled: true,
  });

  let autoplayTimer = null;
  let isHovering = false;

  function stopAutoplay() {
    if (autoplayTimer) {
      clearInterval(autoplayTimer);
      autoplayTimer = null;
    }
  }

  function startAutoplay() {
    stopAutoplay();
    autoplayTimer = setInterval(() => {
      if (!isHovering && !document.hidden) {
        carousel.nextSlide();
      }
    }, 3800);
  }

  function resetAutoplay() {
    stopAutoplay();
    startAutoplay();
  }

  root.addEventListener("mouseenter", () => {
    isHovering = true;
  });

  root.addEventListener("mouseleave", () => {
    isHovering = false;
  });

  root.addEventListener("touchstart", () => {
    isHovering = true;
    resetAutoplay();
  }, { passive: true });

  root.addEventListener("touchend", () => {
    isHovering = false;
    resetAutoplay();
  }, { passive: true });

  root.querySelectorAll(".carousel-arrow").forEach((button) => {
    button.addEventListener("click", resetAutoplay);
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      stopAutoplay();
      return;
    }
    startAutoplay();
  });

  startAutoplay();
  return carousel;
}

function initCategoryTabs() {
  const section = document.querySelector(".product-categories-section");
  if (!section) return;

  const tags = section.querySelectorAll(".pill-group .tag");
  const title = section.querySelector("#category-title");
  const description = section.querySelector("#category-description");
  const list = section.querySelector("#category-list");
  const content = section.querySelector(".category-content");
  const track = section.querySelector(".slider-track");

  const iconUrl = section.getAttribute("data-check-icon");

  const fallbackImage1 = section.getAttribute("data-fallback-image-1") || "";
  const fallbackImage2 = section.getAttribute("data-fallback-image-2") || "";
  const fallbackImage3 = section.getAttribute("data-fallback-image-3") || "";
  const fallbackImage4 = section.getAttribute("data-fallback-image-4") || "";

  const fallbackSlides = {
    sport: [fallbackImage1, fallbackImage2, fallbackImage3, fallbackImage4, fallbackImage2, fallbackImage3],
    weight: [fallbackImage2, fallbackImage1, fallbackImage4, fallbackImage3, fallbackImage4, fallbackImage2],
    skin: [fallbackImage3, fallbackImage4, fallbackImage1, fallbackImage2, fallbackImage3, fallbackImage4],
    snacks: [fallbackImage4, fallbackImage3, fallbackImage2, fallbackImage1, fallbackImage2, fallbackImage4],
    wellness: [fallbackImage1, fallbackImage3, fallbackImage4, fallbackImage2, fallbackImage3, fallbackImage2],
    activewear: [fallbackImage2, fallbackImage4, fallbackImage3, fallbackImage1, fallbackImage4, fallbackImage2]
  };

  const fallbackAliases = {
    sportsupplement: "sport",
    "sport-supplement": "sport",

    weightmanagement: "weight",
    "weight-management": "weight",

    skincarebeauty: "skin",
    "skin-care-beauty": "skin",

    healthysnacks: "snacks",
    "healthy-snacks": "snacks",

    healthwellness: "wellness",
    "health-wellness": "wellness",

    activewearaccessories: "activewear",
    "active-wear-accessories": "activewear"
  };

  if (!tags.length || !title || !description || !list || !track) {
    return;
  }

  function decodeHtml(value) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value || "";
    return textarea.value;
  }

  function normalizeKey(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/&amp;|&/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function resolveCategoryKey(tag) {
    const directKey = normalizeKey(tag.dataset.category || tag.textContent);

    return fallbackAliases[directKey] || directKey || "sport";
  }

  /*
   * Get translated category content from REAL HTML text nodes
   * (so Translate & Adapt can translate them).
   * Falls back to data-* attributes for the hard-coded tags that
   * are rendered when the section has no blocks.
   */
  function getCategoryData(tag) {
    const titleElement = tag.querySelector(".category-title-data");
    const descriptionElement = tag.querySelector(".category-description-data");
    const checklistElement = tag.querySelector(".category-checklist-data");

    return {
      title: titleElement
        ? titleElement.textContent.trim()
        : (tag.dataset.title || "").trim(),

      description: descriptionElement
        ? descriptionElement.textContent.trim()
        : (tag.dataset.description || "").trim(),

      checklist: checklistElement
        ? checklistElement.innerHTML.trim()
        : (tag.dataset.checklist || "").trim()
    };
  }

  function renderChecklist(value) {
    const cleanValue = decodeHtml(value);

    const items = cleanValue
      .split(/<br\s*\/?>|\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);

    list.innerHTML = "";

    items.forEach((item) => {
      const li = document.createElement("li");

      const img = document.createElement("img");

      img.src = iconUrl;
      img.alt = "Check";
      img.width = 20;
      img.height = 20;
      img.loading = "lazy";

      li.appendChild(img);
      li.appendChild(document.createTextNode(" " + item));

      list.appendChild(li);
    });
  }

  function renderSlides(tag) {
    const data = getCategoryData(tag);

    const baseAlt =
      data.title ||
      tag.querySelector(".category-tag-text")?.textContent.trim() ||
      "";

    // Tabs fed by a Shopify collection carry data-source="collection"
    const isCollection = tag.dataset.source === "collection";

    const slides = [];

    for (let i = 1; i <= 6; i++) {
      const src = tag.getAttribute("data-image-" + i);

      if (src) {
        slides.push({
          src,
          alt: tag.getAttribute("data-alt-" + i) || baseAlt
        });
      }
    }

    // Pad with static placeholders only for manual (non-collection) tabs,
    // or when a tab ended up with no images at all.
    if (!isCollection || !slides.length) {
      const fallback =
        fallbackSlides[resolveCategoryKey(tag)] || fallbackSlides.sport;

      for (let i = 0; slides.length < 6 && fallback.length; i++) {
        const src = fallback[i % fallback.length];

        if (!src) break;

        slides.push({ src, alt: baseAlt });
      }
    }

    if (!slides.length) return;

    track.innerHTML = "";

    slides.forEach(({ src, alt }) => {
      const slide = document.createElement("div");
      slide.className = "slider-slide";

      const img = document.createElement("img");

      img.src = src;
      img.alt = alt;
      img.loading = "lazy";

      slide.appendChild(img);
      track.appendChild(slide);
    });

    if (typeof window.WhitefitRefreshCategorySlider === "function") {
      window.WhitefitRefreshCategorySlider();
    }
  }

  tags.forEach((tag) => {
    tag.addEventListener("click", () => {
      if (typeof window.WhitefitPauseCategorySlider === "function") {
        window.WhitefitPauseCategorySlider();
      }

      tags.forEach((item) => {
        item.classList.remove("tag-active");
      });

      tag.classList.add("tag-active");

      if (content) {
        content.classList.add("is-changing");
      }

      setTimeout(() => {
        const data = getCategoryData(tag);

        title.textContent = data.title || "";
        description.textContent = data.description || "";

        renderChecklist(data.checklist || "");
        renderSlides(tag);

        if (content) {
          content.classList.remove("is-changing");
        }
      }, 150);
    });
  });
}

document.addEventListener("DOMContentLoaded", () => {
  initWhoSlider();
  initProductsCarousel();
  initCategoriesSlider();
  initSuccessStoriesSlider();
  initCategoryTabs();
});